import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Layers, Store, Timer, PoundSterling, Percent, PlusCircle, ChevronRight, Mail, Settings2, Loader2, Users, Bell, Inbox, HardHat, Banknote, Rocket, ClipboardCheck, AlertTriangle } from "lucide-react";
import { api, gbp, formatApiError, API } from "@/lib/api";
import RetailerLayout from "@/components/RetailerLayout";

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [retailers, setRetailers] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  async function load() {
    const [o, r, l] = await Promise.all([
      api.get("/admin/overview"),
      api.get("/admin/retailers"),
      api.get("/admin/listings"),
    ]);
    setData(o.data);
    setRetailers(r.data.retailers || []);
    setListings(l.data.listings || []);
  }
  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  return (
    <RetailerLayout>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-overview-page">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h1 className="font-display font-bold text-2xl text-slate-900">Marketplace overview</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/admin/emails"
              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md text-slate-700 hover:bg-slate-100"
              data-testid="admin-email-preview-link"
            >
              <Mail className="w-4 h-4" /> Email preview
            </Link>
            <button
              type="button"
              onClick={() => setShowNew(true)}
              className="fm-btn-primary inline-flex items-center gap-2"
              data-testid="new-retailer-btn"
            >
              <PlusCircle className="w-4 h-4" /> New retailer
            </button>
          </div>
        </div>
        {loading ? (
          <div className="text-slate-500">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <Kpi icon={<Store className="w-4 h-4" />} label="Retailers" value={data.retailers} testId="kpi-retailers" />
              <Kpi icon={<Layers className="w-4 h-4" />} label="Total listings" value={data.listings_total} testId="kpi-listings" />
              <Kpi icon={<Timer className="w-4 h-4" />} label="Reservations" value={data.reservations_total} testId="kpi-reservations" />
              <Kpi icon={<PoundSterling className="w-4 h-4" />} label="GMV" value={gbp(data.gmv_gbp)} testId="kpi-gmv" />
              <Kpi
                icon={<Percent className="w-4 h-4" />}
                label={`Commission (${((data.commission_rate || 0) * 100).toFixed(0)}%)`}
                value={gbp(data.commission_gbp)}
                testId="kpi-commission"
              />
            </div>

            {/* Phase 2 — marketplace activity */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="admin-activity-row">
              <Kpi icon={<Users className="w-4 h-4" />} label="Customers" value={data.customers ?? 0} testId="kpi-customers" />
              <Kpi icon={<Inbox className="w-4 h-4" />} label="Active requests" value={data.requests_active ?? 0} testId="kpi-requests" />
              <Kpi icon={<Bell className="w-4 h-4" />} label="Active alerts" value={data.alerts_active ?? 0} testId="kpi-alerts" />
              <Kpi icon={<Layers className="w-4 h-4" />} label="Plans" value={data.projects_total ?? 0} testId="kpi-projects" />
              <Kpi icon={<HardHat className="w-4 h-4" />} label="Fitters" value={data.fitters_total ?? 0} testId="kpi-fitters" />
              <Kpi icon={<Banknote className="w-4 h-4" />} label="Payouts owed" value={gbp(data.retailer_payout_owed_gbp || 0)} testId="kpi-payouts-owed" />
            </div>

            {(data.retailers_paused > 0 || data.retailers_founder > 0 || data.retailers_payouts_ready > 0) && (
              <div className="flex flex-wrap gap-2 mb-6 text-xs" data-testid="admin-retailer-flags">
                {data.retailers_paused > 0 && <span className="rounded-full px-3 py-1 bg-red-100 text-red-800 font-semibold">{data.retailers_paused} paused</span>}
                {data.retailers_founder > 0 && <span className="rounded-full px-3 py-1 bg-[#E05915]/10 text-[#E05915] font-semibold">{data.retailers_founder} founder (0% fee)</span>}
                {data.retailers_payouts_ready > 0 && <span className="rounded-full px-3 py-1 bg-emerald-100 text-emerald-800 font-semibold">{data.retailers_payouts_ready} payouts ready</span>}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Panel title="Listings by status">
                <ul className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(data.listings_by_status).map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between px-3 py-2 rounded-md bg-slate-50 border border-slate-200 capitalize">
                      <span>{k}</span>
                      <strong className="text-slate-900">{v}</strong>
                    </li>
                  ))}
                </ul>
              </Panel>
              <Panel title={`Retailers (${retailers.length})`}>
                <ul className="text-sm divide-y divide-slate-100" data-testid="retailers-list">
                  {retailers.map((r) => (
                    <li key={r.id}>
                      <Link
                        to={`/admin/retailers/${r.id}`}
                        className="py-2.5 px-1 flex items-center justify-between gap-2 hover:bg-slate-50 rounded-md transition-colors"
                        data-testid={`retailer-row-${r.id}`}
                      >
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{r.name}</div>
                          <div className="text-xs text-slate-500">
                            {r.district || "Greater Manchester"} · {r.postcode || "—"}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {!r.is_claimed && (
                            <span className="text-[10px] font-semibold bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">
                              Unclaimed
                            </span>
                          )}
                          <span className="text-xs bg-slate-100 rounded-full px-2 py-0.5">
                            {r.listings_count} listings
                          </span>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Panel>
            </div>

            <div className="mt-6">
              <LaunchPhasePanel />
            </div>

            <div className="mt-6">
              <LaunchReadinessPanel />
            </div>

            <div className="mt-6">
              <RequestSettingsPanel />
            </div>

            <div className="mt-6">
              <StockAlertsPanel />
            </div>

            <div className="mt-6">
              <AnalyticsPanel />
            </div>

            <div className="mt-6">
              <PaymentsModePanel />
            </div>

            <div className="mt-6">
              <RequestModerationPanel />
            </div>

            <div className="mt-6">
              <Panel title="Latest listings">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="py-2">Ref</th>
                        <th>Item</th>
                        <th>Status</th>
                        <th>Price</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listings.slice(0, 15).map((l) => (
                        <tr key={l.id} className="border-t border-slate-100">
                          <td className="py-2 font-mono text-xs">{l.reference_code}</td>
                          <td className="capitalize">
                            {l.colour} {l.flooring_type.replace("_", " ")}
                          </td>
                          <td className="capitalize">{l.status}</td>
                          <td>{gbp(l.price_gbp)}</td>
                          <td className="text-xs text-slate-500">
                            {new Date(l.created_at).toLocaleDateString("en-GB")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </div>
          </>
        )}
        {showNew && <NewRetailerModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      </main>
    </RetailerLayout>
  );
}

function LaunchPhasePanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState("");
  async function load() {
    const r = await api.get("/admin/settings");
    setSettings(r.data);
    setDays(String(r.data.stock_reminder_days ?? 7));
  }
  useEffect(() => { load(); }, []);
  async function setPhase(phase) {
    const goingLive = phase === "live";
    const msg = goingLive
      ? "Go LIVE to customers? This opens real customer reservations and checkout. Only do this once you have enough useful, current stock."
      : "Switch back to retailer preparation? New real customer purchases will be paused (test checkout stays available).";
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const r = await api.patch("/admin/settings", { launch_phase: phase });
      setSettings(r.data);
      toast.success(goingLive ? "FloorMatch is now LIVE to customers" : "Back in retailer preparation");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  }
  async function saveDays() {
    const n = parseInt(days, 10);
    if (!Number.isFinite(n) || n < 1 || n > 90) { toast.error("Enter 1–90 days"); return; }
    setSaving(true);
    try {
      const r = await api.patch("/admin/settings", { stock_reminder_days: n });
      setSettings(r.data);
      toast.success(`Stock check reminder set to every ${n} days`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  }
  if (!settings) return null;
  const live = settings.launch_phase === "live";
  return (
    <Panel title="Launch phase">
      <div data-testid="launch-phase-panel" className="space-y-4">
        <div className="flex items-start gap-3">
          <span className={`grid place-items-center w-11 h-11 rounded-xl shrink-0 ${live ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            <Rocket className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-900">
                {live ? "Live to customers" : "Retailer preparation"}
              </span>
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${live ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="launch-phase-state">
                {live ? "LIVE" : "PREPARATION"}
              </span>
            </div>
            <p className="text-sm text-slate-600 mt-1">
              {live
                ? "Customers can search, reserve and check out real stock. Switch back to preparation to pause new real purchases if needed."
                : "Retailers register and build their catalogue. New real customer purchases are paused (test checkout still works). Allow roughly eight weeks to build stock — this is an indicative planning period, not an automatic or promised launch date. You launch manually when enough useful, current stock is live."}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setPhase("preparation")}
            disabled={saving || !live}
            className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold transition-colors disabled:opacity-50 ${!live ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            data-testid="set-phase-preparation"
          >
            Retailer preparation
          </button>
          <button
            onClick={() => setPhase("live")}
            disabled={saving || live}
            className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold transition-colors disabled:opacity-50 ${live ? "bg-emerald-600 text-white" : "bg-[#0F3D2E] text-white hover:bg-[#0c3325]"}`}
            data-testid="set-phase-live"
          >
            <Rocket className="w-4 h-4" /> Go live to customers
          </button>
        </div>
        <div className="border-t border-slate-100 pt-4">
          <label className="text-sm font-semibold text-slate-700">Stock-check reminder interval</label>
          <p className="text-xs text-slate-500 mt-0.5 mb-2">How often retailers are nudged (in-app) to confirm stock is still available. Separate from the onboarding window.</p>
          <div className="flex items-center gap-2">
            <input
              type="number" min="1" max="90" value={days}
              onChange={(e) => setDays(e.target.value)}
              className="w-24 h-10 rounded-lg border border-slate-300 px-3 text-sm"
              data-testid="reminder-days-input"
            />
            <span className="text-sm text-slate-500">days</span>
            <button onClick={saveDays} disabled={saving} className="h-10 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50" data-testid="save-reminder-days">
              Save
            </button>
          </div>
        </div>
      </div>
    </Panel>
  );
}

function LaunchReadinessPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/launch-readiness");
      setData(r.data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);
  return (
    <Panel title="Launch readiness">
      <div data-testid="launch-readiness-panel">
        <p className="text-sm text-slate-600 mb-4">
          Real stock signal to support your go-live decision. Demo, test, sold and reserved items are excluded.
          There is no automatic “ready” threshold — you decide when the coverage is useful enough.
        </p>
        {loading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : !data ? (
          <div className="text-slate-500 text-sm">Couldn’t load readiness metrics.</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Metric icon={<Store className="w-4 h-4" />} label="Active retailers" value={data.active_retailers} testId="lr-active-retailers" />
              <Metric icon={<Store className="w-4 h-4" />} label="With live stock" value={data.retailers_with_live_stock} testId="lr-retailers-stock" />
              <Metric icon={<Layers className="w-4 h-4" />} label="Available listings" value={data.available_real_listings} testId="lr-available" />
              <Metric icon={<ClipboardCheck className="w-4 h-4" />} label={`Confirmed ≤${data.stock_reminder_days}d`} value={data.recently_confirmed} testId="lr-recent" />
            </div>
            {(data.overdue_or_unconfirmed > 0 || data.never_confirmed > 0) && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="lr-overdue">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {data.overdue_or_unconfirmed} listing(s) overdue or unconfirmed{data.never_confirmed ? ` · ${data.never_confirmed} never confirmed` : ""}. Freshness is retailer-driven; no stock is auto-removed.
              </div>
            )}
            <CoverageBlock title="Coverage by area" rows={data.coverage_by_area.map((x) => ({ k: x.area, v: x.listings }))} empty="No real listings yet." />
            <CoverageBlock title="Coverage by flooring type" rows={data.coverage_by_type.map((x) => ({ k: (x.type || "other").replace(/_/g, " "), v: x.listings }))} empty="No real listings yet." />
            <CoverageBlock title="Room-size suitability (indicative)" rows={data.coverage_by_room_size.map((x) => ({ k: x.label, v: x.listings }))} />
            {Array.isArray(data.coverage_by_borough) && (
              <BoroughCoverage rows={data.coverage_by_borough} />
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}

function BoroughCoverage({ rows }) {
  const gaps = rows.filter((r) => r.listings === 0).length;
  return (
    <div data-testid="coverage-by-borough">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Coverage by Greater Manchester borough</div>
      <p className="text-[11px] text-slate-400 mb-2">
        Where to recruit next — {gaps} of {rows.length} boroughs have no real stock yet. Not a launch gate.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {rows.map((r) => {
          const gap = r.listings === 0;
          const thin = r.listings > 0 && r.listings <= 2;
          const cls = gap
            ? "border-red-200 bg-red-50"
            : thin
            ? "border-amber-200 bg-amber-50"
            : "border-emerald-200 bg-emerald-50";
          return (
            <div key={r.borough} className={`rounded-lg border px-3 py-2 ${cls}`} data-testid={`borough-${r.borough.toLowerCase()}`}>
              <div className="text-xs font-semibold text-slate-800">{r.borough}</div>
              <div className="flex items-center justify-between mt-0.5">
                <span className="font-display font-bold text-slate-900">{r.listings}</span>
                <span className={`text-[10px] font-semibold ${gap ? "text-red-600" : thin ? "text-amber-700" : "text-emerald-700"}`}>
                  {gap ? "No stock" : thin ? "Thin" : "OK"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Metric({ icon, label, value, testId }) {
  return (
    <div className="bg-slate-50 rounded-xl p-3" data-testid={testId}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">{icon}{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function CoverageBlock({ title, rows, empty }) {
  const has = rows && rows.some((r) => r.v > 0);
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{title}</div>
      {!has && empty ? (
        <div className="text-sm text-slate-400">{empty}</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {rows.map((r) => (
            <span key={r.k} className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 rounded-full px-3 py-1.5 capitalize">
              <span className="text-slate-700 font-medium">{r.k}</span>
              <span className="font-bold text-slate-900">{r.v}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Kpi({ icon, label, value, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testId}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 min-w-0">
      <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">{title}</h2>
      {children}
    </section>
  );
}

// ---------- Stage 5: Retailer-requests master switch + expiry policy ----------
function RequestSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [expiry, setExpiry] = useState("");
  const [reminder, setReminder] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    const r = await api.get("/admin/requests-settings");
    setSettings(r.data);
    setExpiry(r.data.request_expiry_days != null ? String(r.data.request_expiry_days) : "");
    setReminder(r.data.request_reminder_days != null ? String(r.data.request_reminder_days) : "");
  }
  useEffect(() => { load(); }, []);

  async function patch(body, successMsg) {
    setSaving(true);
    try {
      const r = await api.patch("/admin/requests-settings", body);
      setSettings(r.data);
      setExpiry(r.data.request_expiry_days != null ? String(r.data.request_expiry_days) : "");
      setReminder(r.data.request_reminder_days != null ? String(r.data.request_reminder_days) : "");
      if (successMsg) toast.success(successMsg);
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function savePolicy() {
    const body = {};
    if (expiry !== "") body.request_expiry_days = Number(expiry);
    body.request_reminder_days = reminder === "" ? 0 : Number(reminder);
    await patch(body, "Request policy saved");
  }

  async function toggleEnabled() {
    await patch({ retailer_requests_enabled: !settings.retailer_requests_enabled },
      !settings.retailer_requests_enabled ? "Retailer requests turned on" : "Retailer requests turned off");
  }

  if (!settings) {
    return (
      <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="request-settings-panel">
        <div className="py-6 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      </section>
    );
  }

  const enabled = settings.retailer_requests_enabled;
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="request-settings-panel">
      <div className="flex items-center gap-2 mb-1">
        <Settings2 className="w-5 h-5 text-[#0F3D2E]" />
        <h2 className="font-display font-semibold text-lg text-slate-900">Customer requests to retailers</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Master switch for the "ask nearby retailers" feature. You must set an expiry policy before turning it on.
      </p>

      <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <div>
          <div className="font-semibold text-slate-900">Retailer requests</div>
          <div className="text-xs text-slate-500">{enabled ? "On — customers can send requests and retailers see them." : "Off — request entry points are hidden site-wide."}</div>
        </div>
        <button
          type="button"
          onClick={toggleEnabled}
          disabled={saving}
          role="switch"
          aria-checked={enabled}
          className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${enabled ? "bg-[#2A5A3B]" : "bg-slate-300"}`}
          data-testid="toggle-retailer-requests"
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <label className="block">
          <span className="fm-label">Request expiry (days)</span>
          <input
            type="number" min={1} max={90}
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            placeholder={`e.g. ${settings.suggested_expiry_days}`}
            className="fm-input"
            data-testid="request-expiry-days"
          />
        </label>
        <label className="block">
          <span className="fm-label">Reminder before expiry (days, optional)</span>
          <input
            type="number" min={0} max={89}
            value={reminder}
            onChange={(e) => setReminder(e.target.value)}
            placeholder="0 = no reminder"
            className="fm-input"
            data-testid="request-reminder-days"
          />
        </label>
      </div>

      <div className="flex items-center justify-between gap-3 mt-4">
        <p className="text-xs text-slate-500">
          {settings.configured
            ? `Policy configured: requests expire after ${settings.request_expiry_days} day${settings.request_expiry_days !== 1 ? "s" : ""}.`
            : "No expiry policy set yet — set one to enable the feature."}
        </p>
        <button
          type="button"
          onClick={savePolicy}
          disabled={saving}
          className="fm-btn-primary disabled:opacity-70 inline-flex items-center gap-2"
          data-testid="save-request-policy"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save policy
        </button>
      </div>
    </section>
  );
}

// Independent stock-alert digest master switch (saved-search email alerts).
function StockAlertsPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  async function load() {
    const r = await api.get("/admin/settings");
    setSettings(r.data);
  }
  useEffect(() => { load(); }, []);
  async function toggle() {
    setSaving(true);
    try {
      const r = await api.patch("/admin/settings", { stock_alerts_enabled: !settings.stock_alerts_enabled });
      setSettings(r.data);
      toast.success(r.data.stock_alerts_enabled ? "Stock alerts turned on" : "Stock alerts turned off");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }
  if (!settings) {
    return (
      <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="stock-alerts-panel">
        <div className="py-6 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      </section>
    );
  }
  const enabled = settings.stock_alerts_enabled;
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="stock-alerts-panel">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-5 h-5 text-[#0F3D2E]" />
        <h2 className="font-display font-semibold text-lg text-slate-900">Stock alert emails</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Master switch for the saved-search email digest. Independent of customer requests to retailers.
      </p>
      <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <div>
          <div className="font-semibold text-slate-900">Stock alerts</div>
          <div className="text-xs text-slate-500">
            {enabled
              ? "On — customers get email digests when matching stock appears for their saved searches."
              : "Off — saved searches are stored but no alert emails are sent."}
          </div>
        </div>
        <button type="button" onClick={toggle} disabled={saving} role="switch" aria-checked={enabled}
          className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${enabled ? "bg-[#2A5A3B]" : "bg-slate-300"}`}
          data-testid="toggle-stock-alerts">
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
    </section>
  );
}

function AnalyticsPanel() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/admin/analytics?date_from=${from}&date_to=${to}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const csvHref = `${API}/admin/analytics/retailers.csv?date_from=${from}&date_to=${to}`;
  const c = data?.current;
  const ch = data?.change_pct || {};
  const fmtPct = (v) => v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`;

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="analytics-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-display font-semibold text-lg text-slate-900">Performance & analytics</h2>
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="fm-input !h-9 !w-auto text-sm" data-testid="analytics-from" />
          <span className="text-slate-400 text-sm">to</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="fm-input !h-9 !w-auto text-sm" data-testid="analytics-to" />
          <button onClick={load} className="h-9 px-3 rounded-md bg-slate-900 text-white text-sm font-semibold" data-testid="analytics-apply">Apply</button>
          <a href={csvHref} className="h-9 px-3 inline-flex items-center rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="analytics-csv">Export CSV</a>
        </div>
      </div>
      {loading || !c ? (
        <div className="py-8 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-kpis">
            <AnStat label="Paid orders" value={c.orders_paid} delta={fmtPct(ch.orders_paid)} />
            <AnStat label="Sales (GMV)" value={gbp(c.gmv_gbp)} delta={fmtPct(ch.gmv_gbp)} />
            <AnStat label="Commission" value={gbp(c.commission_gbp)} delta={fmtPct(ch.commission_gbp)} />
            <AnStat label="Refunds" value={c.refunds_count} delta={fmtPct(ch.refunds_count)} />
            <AnStat label="Avg order value" value={c.aov_gbp == null ? "No sales" : gbp(c.aov_gbp)} delta={fmtPct(ch.aov_gbp)} />
            <AnStat label="Conversion" value={c.conversion_pct == null ? "—" : `${c.conversion_pct}%`} delta={fmtPct(ch.conversion_pct)} />
            <AnStat label="Retailer payout" value={gbp(c.retailer_payout_gbp)} delta={fmtPct(ch.retailer_payout_gbp)} />
            <AnStat label="vs previous" value={`${data.range.days}d period`} />
          </div>

          <h3 className="font-semibold text-slate-900 mt-5 mb-2 text-sm">Retailer performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="analytics-performance-table">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="py-2">Retailer</th><th>Orders</th><th>GMV</th><th>Commission</th><th>Payout</th><th>AOV</th></tr>
              </thead>
              <tbody>
                {data.retailer_performance.length === 0 ? (
                  <tr><td colSpan={6} className="py-4 text-slate-500">No paid orders in this period.</td></tr>
                ) : data.retailer_performance.map((p) => (
                  <tr key={p.retailer_id} className="border-t border-slate-100">
                    <td className="py-2">{p.retailer_name}{p.is_founder && <span className="ml-1 text-[10px] text-[#E05915] font-semibold">0%</span>}</td>
                    <td>{p.orders}</td><td>{gbp(p.gmv_gbp)}</td><td>{gbp(p.commission_gbp)}</td><td>{gbp(p.payout_gbp)}</td><td>{p.aov_gbp == null ? "—" : gbp(p.aov_gbp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-5">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="font-semibold text-slate-900 text-sm mb-1">Search demand</div>
              <div className="text-sm text-slate-600">{data.search_demand.total_searches} searches · {data.search_demand.no_result_searches} found no suitable stock{data.search_demand.no_result_rate_pct != null ? ` (${data.search_demand.no_result_rate_pct}%)` : ""}</div>
              {data.search_demand.top_supply_gaps.length > 0 && (
                <ul className="text-xs text-slate-500 mt-1.5 space-y-0.5">
                  {data.search_demand.top_supply_gaps.slice(0, 5).map((g, i) => (
                    <li key={i}>{g.postcode} · {g.flooring_type || "any"} — {g.searches} searches, no match</li>
                  ))}
                </ul>
              )}
              <div className="text-[11px] text-slate-400 mt-1">Logged from this build onward.</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="font-semibold text-slate-900 text-sm mb-1">Not yet tracked</div>
              <ul className="text-xs text-slate-500 space-y-0.5">
                {Object.entries(data.not_yet_tracked).map(([k, v]) => (
                  <li key={k}><span className="font-medium text-slate-600">{k.replace(/_/g, " ")}:</span> {v}</li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function AnStat({ label, value, delta }) {
  const pos = typeof delta === "string" && delta.startsWith("+");
  const neg = typeof delta === "string" && delta.startsWith("-");
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-display text-lg font-bold text-slate-900">{value}</div>
      {delta && delta !== "—" && (
        <div className={`text-xs font-semibold ${pos ? "text-emerald-600" : neg ? "text-red-600" : "text-slate-400"}`}>{delta} vs prev</div>
      )}
    </div>
  );
}

function PaymentsModePanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  async function load() {
    const r = await api.get("/admin/settings");
    setSettings(r.data);
  }
  useEffect(() => { load(); }, []);
  async function toggle() {
    setSaving(true);
    try {
      const r = await api.patch("/admin/settings", { payments_platform_pilot: !settings.payments_platform_pilot });
      setSettings(r.data);
      toast.success("Payment mode updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }
  if (!settings) return null;
  const pilot = settings.payments_platform_pilot;
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="payments-mode-panel">
      <div className="flex items-center gap-2 mb-1">
        <Banknote className="w-5 h-5 text-[#0F3D2E]" />
        <h2 className="font-display font-semibold text-lg text-slate-900">Payments mode</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Commission: <strong>{((settings.commission_rate || 0) * 100).toFixed(0)}% standard, 0% founder</strong>. Stripe Connect routes each sale to the retailer's own account with the commission kept as a fee.
      </p>
      <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <div>
          <div className="font-semibold text-slate-900">Platform-collect pilot</div>
          <div className="text-xs text-slate-500">
            {pilot
              ? "ON — when a retailer hasn't finished payout setup, FloorMatch collects the payment (pilot). Reconciled manually."
              : "OFF — checkout is BLOCKED for any retailer who isn't payout-ready (no silent collection)."}
          </div>
        </div>
        <button type="button" onClick={toggle} disabled={saving} role="switch" aria-checked={pilot}
          className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${pilot ? "bg-[#2A5A3B]" : "bg-slate-300"}`}
          data-testid="toggle-payments-pilot">
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${pilot ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
    </section>
  );
}

function RequestModerationPanel() {
  const [customers, setCustomers] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [history, setHistory] = useState({});
  const [busy, setBusy] = useState("");

  async function load() {
    try {
      const r = await api.get("/admin/requests/customers");
      setCustomers(r.data.customers || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  async function toggleHistory(cid) {
    if (expanded === cid) { setExpanded(null); return; }
    setExpanded(cid);
    if (!history[cid]) {
      try {
        const r = await api.get(`/admin/requests?customer_id=${cid}`);
        setHistory((h) => ({ ...h, [cid]: r.data.requests || [] }));
      } catch (e) { toast.error(formatApiError(e)); }
    }
  }

  async function toggleBlock(c) {
    setBusy(c.customer_id);
    try {
      await api.post(`/admin/customers/${c.customer_id}/requests-${c.requests_blocked ? "unblock" : "block"}`);
      toast.success(c.requests_blocked ? "Customer can send requests again" : "Customer's requests paused");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  }

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="request-moderation-panel">
      <div className="flex items-center gap-2 mb-1">
        <Inbox className="w-5 h-5 text-[#0F3D2E]" />
        <h2 className="font-display font-semibold text-lg text-slate-900">Customer requests</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">Everyone who has sent a request to retailers. Expand to review their history, or pause a customer who misuses the feature.</p>
      {customers === null ? (
        <div className="py-6 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : customers.length === 0 ? (
        <div className="text-sm text-slate-500 py-4" data-testid="no-requesters">No customer requests yet.</div>
      ) : (
        <div className="divide-y divide-slate-100" data-testid="requesters-list">
          {customers.map((c) => (
            <div key={c.customer_id} className="py-3">
              <div className="flex items-center justify-between gap-3">
                <button onClick={() => toggleHistory(c.customer_id)} className="text-left min-w-0" data-testid={`requester-${c.customer_id}`}>
                  <div className="font-semibold text-slate-900 truncate">{c.name || "—"} <span className="font-normal text-slate-400">· {c.email}</span></div>
                  <div className="text-xs text-slate-500">{c.active_requests} active · {c.total_requests} total · last {c.last_request_at ? new Date(c.last_request_at).toLocaleDateString("en-GB") : "—"}</div>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  {c.requests_blocked && <span className="text-[11px] font-semibold text-red-700 bg-red-100 rounded-full px-2 py-0.5">Paused</span>}
                  <button onClick={() => toggleBlock(c)} disabled={busy === c.customer_id}
                    className={`h-9 px-3 rounded-full border text-sm font-semibold ${c.requests_blocked ? "border-emerald-400 text-emerald-700 bg-emerald-50 hover:bg-emerald-100" : "border-red-300 text-red-700 bg-white hover:bg-red-50"}`}
                    data-testid={`toggle-block-${c.customer_id}`}>
                    {c.requests_blocked ? "Unpause" : "Pause requests"}
                  </button>
                </div>
              </div>
              {expanded === c.customer_id && (
                <div className="mt-2 pl-1 space-y-1" data-testid={`history-${c.customer_id}`}>
                  {(history[c.customer_id] || []).length === 0 ? (
                    <div className="text-xs text-slate-400">Loading…</div>
                  ) : history[c.customer_id].map((req) => (
                    <div key={req.id} className="text-xs text-slate-600 flex items-center justify-between bg-slate-50 rounded px-2 py-1">
                      <span>{req.room_name} · {req.flooring_type} · {req.room_width_m}×{req.room_length_m}m · {req.postcode}</span>
                      <span className="text-slate-400">{req.status} · {req.created_at ? new Date(req.created_at).toLocaleDateString("en-GB") : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function NewRetailerModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [postcode, setPostcode] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post("/admin/retailers", {
        name: name.trim(),
        postcode: postcode.trim().toUpperCase() || null,
        contact_name: contact.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        is_demo: false,
        is_verified: true,
      });
      toast.success(`${name} added`);
      onCreated();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4" role="dialog" aria-modal="true" data-testid="new-retailer-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg border border-slate-200">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-semibold text-xl text-slate-900">Add a retailer</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-new-retailer">✕</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <label className="block">
            <span className="fm-label">Business name</span>
            <input required value={name} onChange={(e) => setName(e.target.value)} className="fm-input" data-testid="nr-name" />
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="fm-label">Contact name</span>
              <input value={contact} onChange={(e) => setContact(e.target.value)} className="fm-input" data-testid="nr-contact" />
            </label>
            <label className="block">
              <span className="fm-label">Postcode (auto-geocoded)</span>
              <input value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} className="fm-input" data-testid="nr-postcode" />
            </label>
            <label className="block">
              <span className="fm-label">Email</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="fm-input" data-testid="nr-email" />
            </label>
            <label className="block">
              <span className="fm-label">Phone</span>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} className="fm-input" data-testid="nr-phone" />
            </label>
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="h-11 px-4 rounded-full border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-sm text-slate-700">Cancel</button>
            <button type="submit" disabled={saving} className="fm-btn-primary disabled:opacity-70" data-testid="nr-save">
              {saving ? "Adding…" : "Add retailer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
